/**
 * Krok UXP3 (P1-6) — „ODESLAT STRANAM" MUSI POZVANKU SKUTECNE ZALOZIT.
 *
 * Rucni pruchod 10. 10. 2026: po „Odeslat stranám" se objevilo „✓ Odesláno
 * stranám ke kontrole.", ale strana dal hlasila „Čeká na odeslání", historie
 * byla prazdna a v zalozce „Klient (magic link)" nebyla zadna pozvanka —
 * prechod stavu smlouvy (`ready_to_send`) byl JEDINA vec, ktera se stala.
 *
 * Tenhle modul je jedno misto, kde z OBOU tlacitek („Odeslat stranám" v panelu
 * podpisu i „Odeslat stranám ke kontrole" v editoru) vznikaji pozvanky, a
 * vraci PRAVDU o tom, co odeslo. Kdyz strana e-mail nema, pozvanka nevznika
 * (nemame kam poslat) a volajici to MUSI rict — tise hlasit uspech je presne
 * ta vada, kvuli ktere modul existuje.
 */

import {
  createClientInvitation,
  type ClientPartyKey,
  type ClientRole,
} from './client-intake-api.js';
import type { Party, PartyInvitationRef } from './contract-presentation.js';

/** Role strany smlouvy → role klienta v pozvance (`/v1/client-invitations`). */
const INVITE_ROLE: Readonly<Record<string, ClientRole>> = {
  prodavajici: 'prodavajici',
  pronajimatel: 'pronajimatel',
  najemce: 'najemce',
  kupujici: 'zajemce-kupujici',
};

export interface PartyInviteOutcome {
  /** Kolik pozvanek skutecne vzniklo. */
  readonly created: number;
  /** Strany, ktere pozvanku uz maji (druha by poslala druhy odkaz). */
  readonly alreadyInvited: number;
  /** Strany bez e-mailu — tam poslat nejde. */
  readonly withoutEmail: number;
  /** Prvni chyba serveru, kdyz zakladani selhalo; jinak `null`. */
  readonly error: string | null;
}

export interface InviteMissingPartiesInput {
  readonly contractId: string;
  readonly caseId: string | null;
  readonly parties: readonly Party[];
  readonly invitations: readonly PartyInvitationRef[];
}

/**
 * Zalozi pozvanku kazde strane, ktera ji jeste nema a ma e-mail.
 *
 * Duplicitu brani i server (`client_intake_invitation_exists`, 409); tady se
 * strana s pozvankou vynechava proto, aby se makler na 409 nedival jako na
 * chybu, kdyz je vsechno v poradku.
 */
export async function inviteMissingParties(
  input: InviteMissingPartiesInput,
): Promise<PartyInviteOutcome> {
  let created = 0;
  let alreadyInvited = 0;
  let withoutEmail = 0;
  let error: string | null = null;
  for (const party of input.parties) {
    const has = input.invitations.some(
      (i) => i.partyKey === party.role && i.partyIndex === party.index,
    );
    if (has) {
      alreadyInvited += 1;
      continue;
    }
    const email = party.values.email.trim();
    const role = INVITE_ROLE[party.role];
    if (email === '' || role === undefined) {
      withoutEmail += 1;
      continue;
    }
    try {
      await createClientInvitation({
        role,
        partyKey: party.role as ClientPartyKey,
        ...(party.index > 0 ? { partyIndex: party.index } : {}),
        recipientName: party.values.jmeno.trim() === '' ? email : party.values.jmeno.trim(),
        recipientEmail: email,
        channel: 'email',
        contractId: input.contractId,
        ...(input.caseId !== null ? { caseId: input.caseId } : {}),
      });
      created += 1;
    } catch (err: unknown) {
      error ??= err instanceof Error ? err.message : String(err);
    }
  }
  return { created, alreadyInvited, withoutEmail, error };
}

/**
 * Prvni strana, ktera jeste pozvanku nema — jeji udaje predvyplni formular
 * „Klient (magic link)". Pruchod 10. 10.: zalozka se ptala na jmeno a e-mail
 * znovu, prestoze strana smlouvy je oboje uz mela.
 */
export function partyToPrefill(
  parties: readonly Party[],
  invitations: readonly PartyInvitationRef[],
): PartyInvitePrefill | null {
  for (const party of parties) {
    if (invitations.some((i) => i.partyKey === party.role && i.partyIndex === party.index)) continue;
    const role = INVITE_ROLE[party.role];
    if (role === undefined) continue;
    if (party.values.jmeno.trim() === '' && party.values.email.trim() === '') continue;
    return {
      role,
      partyKey: party.role as ClientPartyKey,
      partyIndex: party.index,
      recipientName: party.values.jmeno.trim(),
      email: party.values.email.trim(),
      phone: party.values.telefon.trim(),
    };
  }
  return null;
}

export interface PartyInvitePrefill {
  readonly role: ClientRole;
  readonly partyKey: ClientPartyKey;
  readonly partyIndex: number;
  readonly recipientName: string;
  readonly email: string;
  readonly phone: string;
}
